import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  closeIcon as CloseIcon,
  downloadIcon as DownloadIcon,
  fileIcon as FileIcon,
} from "assets/icons";
import { useNotify } from "../../context/NotificationContext";
import {
  downloadStudentCourseOrder,
  earlyExpelStudent,
  fetchStudentCourseOrders,
} from "../../services/studentService/studentService";
import type {
  StudentCourseOrder,
  StudentPurchase,
} from "../../services/studentService/studentService.types";
import { logger } from "../../utils/logger";
import {
  Body,
  CloseButton,
  Confirmation,
  ConfirmationLead,
  ConfirmationList,
  ConfirmationText,
  ContextBlock,
  ContextCopy,
  ContextDate,
  CourseName,
  DangerButton,
  DangerQuietButton,
  DownloadLink,
  Footer,
  FooterRight,
  Header,
  OrderIcon,
  OrderMeta,
  OrderMetaText,
  OrderRow,
  OrderSkeleton,
  OrdersList,
  OrdersSection,
  OrderTitle,
  Overlay,
  QuietButton,
  RetryButton,
  SectionTitle,
  StateBlock,
  StateText,
  StatusChip,
  StudentName,
  Surface,
} from "./StudentOrdersModal.styles";

interface Props {
  purchase: StudentPurchase;
  onClose: () => void;
  onChanged: (purchase: StudentPurchase) => void;
}

type ModalMode = "orders" | "expel_confirm";
type StatusTone = "active" | "ended" | "completed";

const formatDate = (value?: string | null) =>
  value ? new Date(value).toLocaleDateString("ru-RU") : "";

const orderKindLabel = (kind: StudentCourseOrder["kind"]) =>
  kind === "expulsion" ? "отчисление" : "зачисление";

const StudentOrdersModal: React.FC<Props> = ({ purchase, onClose, onChanged }) => {
  const toast = useNotify();
  const [orders, setOrders] = useState<StudentCourseOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [mode, setMode] = useState<ModalMode>("orders");
  const [expelling, setExpelling] = useState(false);

  const studentName = purchase.student.full_name || purchase.student.email || "Ученик";

  const status = useMemo<{ label: string; tone: StatusTone }>(() => {
    if (purchase.end_reason === "early_expulsion") {
      return { label: "Отчислен", tone: "ended" };
    }
    if (purchase.end_reason === "completed") {
      return { label: "Завершён", tone: "completed" };
    }
    return { label: "Учится", tone: "active" };
  }, [purchase.end_reason]);

  const contextDate = useMemo(() => {
    const enrolled = `Зачислен ${formatDate(purchase.created)}`;
    if (!purchase.ended_at) return enrolled;

    const endedLabel = purchase.end_reason === "completed" ? "завершён" : "отчислен";
    return `${enrolled} · ${endedLabel} ${formatDate(purchase.ended_at)}`;
  }, [purchase.created, purchase.ended_at, purchase.end_reason]);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      setOrders(await fetchStudentCourseOrders(purchase.payment_id));
    } catch (error) {
      setOrders([]);
      setLoadError(true);
      logger.error("Ошибка загрузки приказов ученика:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить приказы", life: 3000 });
    } finally {
      setLoading(false);
    }
  }, [purchase.payment_id, toast]);

  useEffect(() => {
    setMode("orders");
    void loadOrders();
  }, [loadOrders, purchase.payment_id]);

  const downloadOrder = async (order: StudentCourseOrder) => {
    try {
      await downloadStudentCourseOrder(order);
    } catch (error) {
      logger.error("Ошибка скачивания приказа ученика:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось скачать приказ", life: 3000 });
    }
  };

  const expel = async () => {
    setExpelling(true);
    try {
      const result = await earlyExpelStudent(purchase.payment_id);
      const updated = {
        ...purchase,
        course_access: result.course_access,
        ended_at: result.ended_at,
        end_reason: result.end_reason,
      };
      onChanged(updated);
      setMode("orders");
      await loadOrders();
      toast.show({ severity: "success", summary: "Готово", detail: "Ученик досрочно отчислен, приказ сформирован", life: 4000 });
    } catch (error) {
      logger.error("Ошибка досрочного отчисления:", error);
      toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось выполнить досрочное отчисление", life: 4000 });
    } finally {
      setExpelling(false);
    }
  };

  const closeOrReturn = () => {
    if (expelling) return;
    if (mode === "expel_confirm") {
      setMode("orders");
      return;
    }
    onClose();
  };

  const dialogTitle = mode === "orders" ? "Приказы по курсу" : "Досрочное отчисление";
  const sectionTitle =
    !loading && !loadError && orders.length > 0
      ? `Сформированные приказы · ${orders.length}`
      : "Сформированные приказы";

  return (
    <Overlay onMouseDown={(event) => event.target === event.currentTarget && closeOrReturn()}>
      <Surface
        role="dialog"
        aria-modal="true"
        aria-labelledby="student-orders-title"
        aria-describedby={mode === "expel_confirm" ? "student-expel-description" : undefined}
      >
        <Header>
          <h2 id="student-orders-title">{dialogTitle}</h2>
          <CloseButton type="button" aria-label="Закрыть" onClick={closeOrReturn} disabled={expelling}>
            <CloseIcon aria-hidden="true" />
          </CloseButton>
        </Header>

        <ContextBlock>
          <ContextCopy>
            <StudentName>{studentName}</StudentName>
            <CourseName>{purchase.course.title}</CourseName>
            <ContextDate>{contextDate}</ContextDate>
          </ContextCopy>
          <StatusChip $tone={status.tone}>{status.label}</StatusChip>
        </ContextBlock>

        <Body>
          {mode === "orders" ? (
            <OrdersSection aria-labelledby="orders-section-title">
              <SectionTitle id="orders-section-title">{sectionTitle}</SectionTitle>
              {loading ? (
                <OrdersList aria-label="Загрузка приказов" aria-live="polite">
                  <OrderSkeleton />
                  <OrderSkeleton />
                </OrdersList>
              ) : loadError ? (
                <StateBlock>
                  <StateText $danger>Не удалось загрузить приказы</StateText>
                  <RetryButton type="button" variant="secondary" onClick={() => void loadOrders()}>
                    Повторить
                  </RetryButton>
                </StateBlock>
              ) : orders.length === 0 ? (
                <StateBlock>
                  <StateText>
                    Приказов пока нет. Они формируются автоматически при зачислении и отчислении.
                  </StateText>
                </StateBlock>
              ) : (
                <OrdersList>
                  {orders.map((order) => (
                    <OrderRow key={order.id}>
                      <OrderIcon $danger={order.kind === "expulsion"}>
                        <FileIcon aria-hidden="true" />
                      </OrderIcon>
                      <OrderMeta>
                        <OrderTitle>{order.title}</OrderTitle>
                        <OrderMetaText>
                          {orderKindLabel(order.kind)} · {formatDate(order.created)}
                        </OrderMetaText>
                      </OrderMeta>
                      <DownloadLink
                        href={order.download_url}
                        aria-label={`Скачать ${order.title}`}
                        onClick={(event) => {
                          event.preventDefault();
                          void downloadOrder(order);
                        }}
                      >
                        <DownloadIcon aria-hidden="true" />
                        Скачать
                      </DownloadLink>
                    </OrderRow>
                  ))}
                </OrdersList>
              )}
            </OrdersSection>
          ) : (
            <Confirmation id="student-expel-description">
              <ConfirmationLead>Отчислить ученика с курса?</ConfirmationLead>
              <ConfirmationText>
                Действие используется вместо обычного завершения курса и отменить его нельзя.
              </ConfirmationText>
              <ConfirmationList>
                <li>{studentName} потеряет доступ к курсу «{purchase.course.title}»</li>
                <li>Дата и основание зафиксируются в карточке ученика</li>
                <li>Система сформирует приказ о досрочном отчислении, он появится в этом списке</li>
              </ConfirmationList>
            </Confirmation>
          )}
        </Body>

        <Footer>
          {mode === "orders" ? (
            <>
              {!purchase.ended_at && (
                <DangerQuietButton
                  type="button"
                  variant="dangerSecondary"
                  onClick={() => setMode("expel_confirm")}
                  disabled={loading}
                >
                  Досрочно отчислить
                </DangerQuietButton>
              )}
              <FooterRight>
                <QuietButton type="button" variant="secondary" onClick={onClose}>
                  Закрыть
                </QuietButton>
              </FooterRight>
            </>
          ) : (
            <>
              <QuietButton
                type="button"
                variant="secondary"
                onClick={() => setMode("orders")}
                disabled={expelling}
              >
                Назад
              </QuietButton>
              <FooterRight>
                <DangerButton
                  type="button"
                  variant="danger"
                  onClick={() => void expel()}
                  loading={expelling}
                  disabled={expelling}
                >
                  {expelling ? "Отчисляем" : "Отчислить"}
                </DangerButton>
              </FooterRight>
            </>
          )}
        </Footer>
      </Surface>
    </Overlay>
  );
};

export default StudentOrdersModal;